import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, Observable, of } from 'rxjs';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasElementId,
  CanvasElementProperties,
  CanvasElementType,
  getRootElementId,
  LayoutData,
  NavigationContainerVariantType,
} from '@blaze-runtime/engine-domain';
import {
  CanvasElementPropertiesWithData,
  RuntimePage,
  TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { isNavigationPageType } from '@blaze-shared/projects-domain';
import { StyleSheetsFacadeFromStyles } from '@blaze-shared/style-state-feature';
import { StyleSheet } from '@blaze-shared/theming-domain';

import {
  hasProps,
  hasSlots,
  hasStylesConfig,
  hasWrapperRefs,
} from '../../canvas-elements/element-model/element-props-guards';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { ROOT_HEADER_SLOT, ROOT_MAIN_SLOT } from '../../elements/root/root.models';
import { WRAPPER_HEADER_SLOT_ID } from '../../elements/wrapper/wrapper.models';
import { elementsToEntityState } from '../../utils/elements/element.utils';
import { getPathFirstInSlot } from '../../utils/layout/helpers';
import { placeElementMutably } from '../../utils/layout/place-element';
import { removeElementFromParentMutably } from '../../utils/layout/remove-element';
import { copyPropsFromInactiveStyleSheetMutably } from '../styles';

// import { LayoutMigrationRunnerService } from './migrations/layout-migration-runner.service';
import { assignMissingParentNodes } from './missing-parent-node';

export const elementProps = (
  element: CanvasElement,
): CanvasElementProperties & CanvasElementPropertiesWithData =>
  (hasProps(element) ? element.props : {}) as CanvasElementProperties &
    CanvasElementPropertiesWithData;

@Injectable({ providedIn: 'root' })
export class LayoutNormalizerService {
  private readonly store = inject(Store);
  private readonly styleSheetsFacade = inject(StyleSheetsFacadeFromStyles);
  // private readonly migrationRunner = inject(LayoutMigrationRunnerService);

  normalize(layout: LayoutData): Observable<LayoutData> {
    return of(layout).pipe(
      concatLatestFrom(() => [
        this.styleSheetsFacade.entities$,
        this.store.select(runtimeSelectors.selectRuntimePages),
      ]),
      map(([layout, styleSheets, pages]) => {
        this.normalizeNavContainerMutably(layout, pages);
        assignMissingParentNodes(layout.uiLayer.content);
        this.normalizeElementsMutably(layout, styleSheets);
        return layout;
      }),
    );
  }

  normalizeElementsMutably(layout: LayoutData, styleSheets: Dictionary<StyleSheet>): void {
    const elements = layout.uiLayer.content;

    for (const element of elements) {
      let props = elementProps(element);

      if (props.data?.length) {
        for (const binding of props.data) {
          // binding doesn't have the enabled flag or has it disabled but comp can only have dynamic source
          if (
            !('enabled' in binding) ||
            binding.enabled === undefined ||
            (['table', 'list'].includes(element.type) && !binding.enabled)
          ) {
            binding.enabled = true;
          }
        }
      }

      /**
       * Copy props to element if used style sheet has been deleted
       * StyleSheet.active === false
       */
      if (hasStylesConfig(element) && element.props.styleConfig?.styleSheetId) {
        const styleSheetId = element.props.styleConfig?.styleSheetId;
        const styleSheet = styleSheets[styleSheetId];

        if (styleSheet && !styleSheet?.active) {
          copyPropsFromInactiveStyleSheetMutably(element, styleSheet.data[0]);
          props = element?.props;
        }
      }

      this.updateFormElementHeightMutably(element, props);

      /**
       * Move existing navigation containers into 'header' slot of it's root element.
       */
      if (isElementType(element, 'navContainer')) {
        if (element.parentNode && element.parentNode.slot === ROOT_MAIN_SLOT) {
          const entityState = elementsToEntityState(elements);
          removeElementFromParentMutably(entityState, element.id);

          const rootElementId: CanvasElementId = getRootElementId(element.ns);
          const rootElement = entityState.entities[rootElementId];

          if (!hasSlots(rootElement)) {
            throw new Error(`Cannot find root element or slots for element id ${element.id}`);
          }

          // If header slot is missing in root element - add it.
          if (!rootElement.slots.ids.includes(ROOT_HEADER_SLOT)) {
            rootElement.slots.ids.push(ROOT_HEADER_SLOT);
            rootElement.slots[ROOT_HEADER_SLOT] = {
              children: [],
              sizing: {},
            };
          }

          const targetPath = getPathFirstInSlot(entityState, rootElementId, ROOT_HEADER_SLOT);
          placeElementMutably(entityState, { elementId: element.id, atPath: targetPath });
        }
      }

      if (isElementType(element, 'wrapper')) {
        const mainChildren = element.slots.main?.children;
        if (mainChildren && mainChildren.length > 1) {
          const entityState = elementsToEntityState(elements);
          const wrappedElementId = mainChildren.find((id) =>
            hasWrapperRefs(entityState.entities[id]),
          );
          const idsToMove = mainChildren.filter((id) => id !== wrappedElementId);

          if (!hasSlots(element)) {
            throw new Error(`Wrapper element error - no slots found for element id ${element.id}`);
          }

          // If header slot is missing in wrapper element - add it.
          if (!element.slots.ids.includes(WRAPPER_HEADER_SLOT_ID)) {
            element.slots.ids.push(WRAPPER_HEADER_SLOT_ID);
            element.slots[WRAPPER_HEADER_SLOT_ID] = { children: [] };
          }

          // Move non-main elements from wrapper main slot to header slot.
          idsToMove.forEach((id) => {
            removeElementFromParentMutably(entityState, id);
            const targetPath = getPathFirstInSlot(entityState, element.id, WRAPPER_HEADER_SLOT_ID);
            placeElementMutably(entityState, { elementId: id, atPath: targetPath });
          });
        }
      }
    }
  }

  normalizeNavContainerMutably(layout: LayoutData, pages: Dictionary<RuntimePage>): void {
    const page = pages[layout.pageId];

    if (!page) {
      console.warn(`No page found for ${layout.pageId}. Layout could not be upgraded!`);
      return;
    }

    const { pageType } = page;

    // Only navigation pages should be handled
    if (!isNavigationPageType(pageType)) {
      return;
    }

    const layoutElements = layout.uiLayer.content;
    let expectedNavigationElementContainerType: NavigationContainerVariantType | undefined;

    if (pageType === 'Header') {
      expectedNavigationElementContainerType = 'navHeaderContainer';
    } else if (pageType === 'Bottombar') {
      expectedNavigationElementContainerType = 'navBottombarContainer';
    } else if (pageType === 'Sidebar') {
      expectedNavigationElementContainerType = 'navSidebarContainer';
    }

    if (!expectedNavigationElementContainerType) {
      return;
    }

    // If migration already happened - skip processing
    if (layoutElements.some((element) => element.type === expectedNavigationElementContainerType)) {
      return;
    }

    const rootElement = layoutElements.find(
      (element) => isElementType(element, 'root') && element.ns === layout.pageId,
    );

    // Root element may be missing when generating default layout for navigation page.
    // Also discard possibly corrupted pages without root element.
    if (!rootElement) {
      return;
    }

    const primaryContainerId = rootElement.slots?.main?.children.find((elementId) =>
      layoutElements.some(
        (element) =>
          element.id === elementId &&
          (element.type === 'container' ||
            // Type 'verticalContainer' is no longer valid but still can exist in backend layout model
            element.type === ('verticalContainer' as any)),
      ),
    );
    const primaryContainer = layoutElements.find((element) => element.id === primaryContainerId);

    if (!primaryContainer) {
      console.error(
        `No primary container found for root ${rootElement.id}. Layout may be corrupted!`,
      );
      return;
    }

    // Upgrade every navigation element primary container
    // Previously containers of navigation elements had same props as container so simply changing
    // element type is enough.
    primaryContainer.type = expectedNavigationElementContainerType;
  }

  /**
   * ALl form elements should be able to display all of its content.
   * Exceptions are textInput and textField where height fixed is used to limit growth of element
   * when using format 'paragraph'.
   * @param element
   * @param props
   * @private
   */
  private updateFormElementHeightMutably(element: CanvasElement, props: any): void {
    const textFormElement: CanvasElementType[] = ['textField', 'textInput'];
    const otherFormElements: CanvasElementType[] = ['datetimeInput', 'dropdown'];

    if (!element.height) {
      return;
    }

    if (
      otherFormElements.includes(element.type) ||
      (textFormElement.includes(element.type) &&
        (props.content?.input?.format as TableColumnFormat) !== 'paragraph')
    ) {
      element.height.fixed = false;
    }
  }
}
